import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/api.ts";
import { CATEGORY_LABEL } from "../lib/categories.ts";
import { OutlineButton } from "./FilterBar.tsx";
import { theme } from "../theme.ts";
import type { NotStockedSettings, NotStockedSettingsResponse } from "@march/shared";

type Draft = { cover_colours: string; parts: string };
const toDraft = (s: NotStockedSettings): Draft => ({ cover_colours: s.cover_colours.join(", "), parts: s.parts.join(", ") });
const codes = (t: string) => [...new Set(t.split(/[\s,;]+/).map((x) => x.trim().toUpperCase()).filter(Boolean))].sort();
const fromDraft = (d: Draft): NotStockedSettings => ({ cover_colours: codes(d.cover_colours), parts: codes(d.parts) });
const same = (a: NotStockedSettings, b: NotStockedSettings) => JSON.stringify(a) === JSON.stringify(b);

const field = {
  width: "100%",
  maxWidth: 640,
  padding: "6px 8px",
  border: `1px solid ${theme.color.border}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.font.mono,
  fontSize: theme.font.size.sm,
  background: "transparent",
  color: "inherit",
  boxSizing: "border-box" as const,
};

/** Parts Operations doesn't keep in stock: left off To order and the AI's buying list. */
export function NotStockedCard() {
  const qc = useQueryClient();
  const q = useQuery<NotStockedSettingsResponse>({
    queryKey: ["not-stocked-settings"],
    queryFn: () => apiGet<NotStockedSettingsResponse>("/api/settings/not-stocked"),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (q.data && !draft) setDraft(toDraft(q.data.settings));
  }, [q.data, draft]);

  if (q.isLoading || !draft) return <div style={{ color: theme.color.textMuted }}>Loading…</div>;
  if (q.error) return <div style={{ color: theme.color.danger }}>{(q.error as Error).message}</div>;
  const d = q.data!;
  const dirty = !same(fromDraft(draft), d.settings);

  const save = async (value: NotStockedSettings) => {
    setSaving(true);
    setMsg(null);
    try {
      await apiPut("/api/settings/not-stocked", value);
      setMsg({ ok: true, text: "Saved. To order, Trends and the AI tools use the new list from the next page load." });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["not-stocked-settings"] }),
        qc.invalidateQueries({ queryKey: ["forecast"] }),
        qc.invalidateQueries({ queryKey: ["trends"] }),
      ]);
      setDraft(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const byType = new Map<string, string[]>();
  for (const m of d.matches) byType.set(m.item_type, [...(byType.get(m.item_type) ?? []), m.itemid]);

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <div style={{ fontSize: theme.font.size.sm, color: theme.color.textMuted, lineHeight: 1.5 }}>
        Parts Operations doesn't keep in stock. They're still forecast and still count in Trends, but they're left off To
        order (with a "Show them" link) and out of the AI agents' buying list. Separate codes with commas or spaces.
      </div>

      <label style={{ display: "grid", gap: 4, fontSize: theme.font.size.sm }}>
        <b>Cover colours</b>
        <span style={{ color: theme.color.textFaint, fontSize: theme.font.size.xs }}>
          Every cover in the colour, any size or vent. F71 covers C908-F71, C118-F71-SWV and so on.
        </span>
        <textarea
          rows={2}
          value={draft.cover_colours}
          onChange={(e) => setDraft({ ...draft, cover_colours: e.target.value })}
          style={field}
        />
      </label>

      <label style={{ display: "grid", gap: 4, fontSize: theme.font.size.sm }}>
        <b>Single parts</b>
        <span style={{ color: theme.color.textFaint, fontSize: theme.font.size.xs }}>Exact part codes, e.g. RL50.</span>
        <textarea rows={2} value={draft.parts} onChange={(e) => setDraft({ ...draft, parts: e.target.value })} style={field} />
      </label>

      <div style={{ display: "flex", gap: theme.space.sm, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => save(fromDraft(draft))}
          style={{
            padding: "6px 14px",
            borderRadius: theme.radius.md,
            border: "none",
            background: dirty ? theme.color.brand : theme.color.border,
            color: theme.color.onBrand,
            fontWeight: 600,
            cursor: dirty && !saving ? "pointer" : "default",
          }}
        >
          {saving ? "Saving…" : "Save not-stocked list"}
        </button>
        {dirty && <OutlineButton onClick={() => setDraft(toDraft(d.settings))}>Discard changes</OutlineButton>}
        {!d.is_default && !dirty && (
          <OutlineButton onClick={() => save(d.defaults)} disabled={same(d.settings, d.defaults)}>
            Reset to defaults
          </OutlineButton>
        )}
        <span style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint }}>
          {d.is_default
            ? "Using Operations' list of 22 Oct 2026"
            : `Last changed by ${d.updated_by ?? "unknown"} on ${new Date(d.updated_at!).toLocaleString()}`}
        </span>
      </div>
      {msg && (
        <div
          style={{
            padding: "8px 10px",
            borderRadius: theme.radius.md,
            background: msg.ok ? theme.color.successFaint : theme.color.dangerFaint,
            color: msg.ok ? theme.color.successText : theme.color.dangerText,
            fontSize: theme.font.size.sm,
          }}
        >
          {msg.text}
        </div>
      )}

      <details style={{ fontSize: theme.font.size.sm }}>
        <summary style={{ cursor: "pointer" }}>
          The saved list marks <b>{d.matches.length}</b> tracked {d.matches.length === 1 ? "part" : "parts"}
        </summary>
        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
          {[...byType.entries()].map(([t, ids]) => (
            <div key={t}>
              <b>{CATEGORY_LABEL[t] ?? t}</b> ({ids.length}):{" "}
              <span style={{ fontFamily: theme.font.mono, fontSize: theme.font.size.xs, color: theme.color.textMuted }}>{ids.join(", ")}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
